import { useCallback, useEffect, useState } from 'react'
import BN from 'bn.js'
import { getOperateIx, MAX_REPAY_AMOUNT } from '@jup-ag/lend/borrow'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import { MINTS, type JupBorrowPortfolio } from '../../api/trade'
import { apiFetch } from '../../api/client'
import { newConnection } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import { useSplBalance } from '../../hooks/useSplBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

const JUPSOL_USDC_VAULT_ID = 13
const USDC_DECIMALS = 6
// SOL needed before submit. Existing position: tx fees + USDC ATA rent (was
// 0.005, bumped to 0.01 to leave headroom for high priority fees during
// network congestion). Brand-new position: also funds the position PDA +
// position NFT mint + token account + Metaplex metadata, ~0.015 SOL on top.
const MIN_SOL_FOR_BORROW      = 0.01
const MIN_SOL_FOR_INIT_BORROW = 0.02

interface BorrowPosition {
  vaultId: number
  positionId: number
  supplyAmount: number
  supplyUsd: number
  supplyApy: number
  borrowAmount: number
  borrowUsd: number
  borrowApr: number
}

function parseUsdc(raw: string): BN | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '000000').slice(0, USDC_DECIMALS)
  try { return new BN((whole || '0') + padded) } catch { return null }
}

interface Props {
  balances: WalletBalance[] | null
  defaultAmount?: string
}

export default function BorrowForm({ balances, defaultAmount }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, buildV0, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()

  const [mode, setMode] = useState<'borrow' | 'repay'>('borrow')
  const [amount, setAmount] = useState(defaultAmount ?? '')
  const [repayAll, setRepayAll] = useState(false)
  const [pos, setPos] = useState<BorrowPosition | null>(null)
  const [apiError, setApiError] = useState(false)
  const [result, setResult] = useState<TradeResult>({ kind: 'idle' })

  const usdcWallet = findBalance(balances, MINTS.USDC)      // display-only merged total
  const splUsdc = useSplBalance(publicKey, MINTS.USDC)        // authoritative wallet ATA for repay

  const reload = useCallback(async () => {
    if (!portfolioId) return
    let data: JupBorrowPortfolio | null
    try {
      data = await apiFetch<JupBorrowPortfolio>(
        `/api/jupiter/lend/portfolio-positions?user=${encodeURIComponent(portfolioId)}`,
      )
      setApiError(false)
    } catch {
      setApiError(true)
      return
    }
    if (!data?.elements) { setPos(null); return }
    const el = data.elements.find(e => e.type === 'borrowlend' && e.data?.link?.includes('/lend/borrow/'))
    if (!el?.data) { setPos(null); return }
    const link = el.data.link ?? ''
    const m = link.match(/\/borrow\/(\d+)\/nfts\/(\d+)/)
    const supply = el.data.suppliedAssets?.[0]?.data
    const borrow = el.data.borrowedAssets?.[0]?.data
    setPos({
      vaultId: m ? parseInt(m[1]) : JUPSOL_USDC_VAULT_ID,
      positionId: m ? parseInt(m[2]) : 0,
      supplyAmount: supply?.amount ?? 0,
      supplyUsd: (el.data.suppliedAssets?.[0] as any)?.value ?? 0,
      supplyApy: supply?.yields?.[0]?.apy ?? 0,
      borrowAmount: borrow?.amount ?? 0,
      borrowUsd: (el.data.borrowedAssets?.[0] as any)?.value ?? 0,
      borrowApr: Math.abs(el.data.borrowedYields?.[0]?.[0]?.apr ?? 0),
    })
  }, [portfolioId])

  useEffect(() => { reload() }, [reload])

  const submit = async () => {
    if (apiError) {
      setResult({ kind: 'error', message: 'Jupiter portfolio API is unreachable — cannot safely determine your existing position. Refresh and retry, or manage directly at jup.ag.' })
      return
    }
    const bn = parseUsdc(amount)
    const isRepayAll = mode === 'repay' && repayAll
    if (!isRepayAll && (!bn || bn.isZero())) return
    setResult({ kind: 'pending', label: 'Building transaction…' })

    try {
      if (!connected || !publicKey) throw new Error('Wallet not connected')
      const signer = publicKey
      if (portfolioId && signer.toBase58() !== portfolioId) {
        throw new Error('Connected wallet does not match your session')
      }

      // Repay pre-flight: typed USDC amount must fit the user's wallet ATA.
      // isRepayAll is safe — the SDK's MAX_REPAY_AMOUNT only moves `min(debt, wallet)`.
      if (mode === 'repay' && !isRepayAll && bn && !splUsdc.loading && bn.gt(splUsdc.raw)) {
        const want = (Number(bn.toString()) / 1e6).toFixed(2)
        const have = (Number(splUsdc.raw.toString()) / 1e6).toFixed(2)
        throw new Error(`Amount ${want} USDC exceeds wallet ATA balance ${have} USDC. Reduce amount or tick "Repay all".`)
      }

      const connection = newConnection()
      const vaultId = pos?.vaultId ?? JUPSOL_USDC_VAULT_ID
      // Pass positionId === 0 for new positions — getOperateIx returns the
      // init ix bundled into `ixs`. Pre-calling getInitPositionIx and passing
      // the resulting nftId here makes getOperateIx fetch a position account
      // that doesn't yet exist on chain (the init ix would be in the same tx,
      // not yet executed) → "Account does not exist or has no data <PDA>".
      const positionId = pos?.positionId ?? 0

      const debtAmount = mode === 'borrow'
        ? (bn as BN)
        : (isRepayAll ? MAX_REPAY_AMOUNT : (bn as BN).neg())

      const { ixs, addressLookupTableAccounts } = await getOperateIx({
        vaultId,
        positionId,
        colAmount: new BN(0),
        debtAmount,
        connection,
        signer,
      })

      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = await buildV0(ixs, addressLookupTableAccounts)
      const submitted = await signAndSubmit(tx)

      const display = isRepayAll ? 'all debt' : `${amount} USDC`
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `${mode === 'borrow' ? 'Borrowed' : 'Repaid'} ${display}`,
      })
      setAmount('')
      setRepayAll(false)
      setTimeout(reload, 4000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const canCloseFully = !!pos && pos.borrowAmount > 0
    && !splUsdc.loading
    && (Number(splUsdc.decimal) || 0) >= pos.borrowAmount

  const onMaxRepay = () => {
    if (!pos || pos.borrowAmount <= 0) return
    // When the wallet can cover the full debt, auto-tick "Repay all". This
    // routes the submit path through MAX_REPAY_AMOUNT so the SDK uses the
    // live on-chain debt (which has accrued more interest by the time the tx
    // lands) and the position closes cleanly with no dust. When the wallet
    // can't cover full debt, fall back to setting the partial amount.
    if (canCloseFully) {
      setRepayAll(true)
      setAmount('')
      return
    }
    const walletUi = Number(splUsdc.decimal) || 0
    setAmount(walletUi.toFixed(6))
  }

  // Native-SOL balance for the rent / fees pre-check on borrow.
  const walletSol         = findBalance(balances, MINTS.SOL)
  const solBalanceNum     = parseFloat(walletSol?.amount ?? '0')
  const needsInitPosition = !pos?.positionId
  const minSolNeeded      = needsInitPosition ? MIN_SOL_FOR_INIT_BORROW : MIN_SOL_FOR_BORROW
  const insufficientSol   = mode === 'borrow' && balances != null && solBalanceNum < minSolNeeded

  const canSubmit = !apiError && (mode === 'repay' && repayAll
    ? !!pos && pos.borrowAmount > 0
    : !!parseUsdc(amount) && !insufficientSol)

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <PosCard
          title="Collateral"
          mint={MINTS.jupSOL}
          symbol="jupSOL"
          amount={pos ? `${pos.supplyAmount.toFixed(3)}` : '—'}
          usd={pos ? `$${pos.supplyUsd.toFixed(2)}` : '—'}
          rate={pos && pos.supplyApy ? `+${(pos.supplyApy * 100).toFixed(1)}% APY` : ''}
          rateClass="text-accent-green"
        />
        <PosCard
          title="Debt"
          mint={MINTS.USDC}
          symbol="USDC"
          amount={pos ? `${pos.borrowAmount.toFixed(2)}` : '—'}
          usd={pos ? `$${pos.borrowUsd.toFixed(2)}` : '—'}
          rate={pos && pos.borrowApr ? `${(pos.borrowApr * 100).toFixed(1)}% APR` : ''}
          rateClass="text-accent-red"
        />
      </div>

      <div className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-[11px]">
        <div className="flex items-center gap-1.5 text-gray-500">
          <TokenIcon symbol="USDC" mint={MINTS.USDC} size={14} />
          Wallet USDC (spendable)
        </div>
        <div className="text-ink">
          {splUsdc.loading ? '…' : formatBalance(splUsdc.decimal, 2)} USDC
          {usdcWallet && (
            <span className="ml-1 text-gray-500">· {formatUsdShort(usdcWallet.usdValue)}</span>
          )}
        </div>
      </div>

      {apiError && (
        <div className="rounded-lg border border-accent-red/30 bg-accent-red/10 px-3 py-2 text-[11px] text-accent-red">
          Portfolio API unreachable — position data could not be loaded. Submitting is blocked to prevent creating a duplicate position. Refresh or retry in a moment.
        </div>
      )}

      {!apiError && !pos?.positionId && (
        <div className="rounded-lg border border-accent-yellow/30 bg-accent-yellow/10 px-3 py-2 text-[11px] text-accent-yellow">
          No borrow position found. The transaction will initialise one — make sure you've already supplied jupSOL collateral via the Lend tab.
        </div>
      )}

      {insufficientSol && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
          Wallet has only {solBalanceNum.toFixed(4)} SOL — this {needsInitPosition ? 'init + borrow' : 'borrow'} needs at least
          {' '}{minSolNeeded} SOL on hand to fund tx fees{needsInitPosition ? ' plus the rent for the new position NFT, position PDA, token account and metadata' : ' plus any new associated token account rent'}. Top up SOL and retry.
        </div>
      )}

      <div className="flex gap-1 rounded-lg bg-surface-hover p-1">
        {(['borrow', 'repay'] as const).map(m => (
          <button key={m}
            onClick={() => { setMode(m); setAmount(''); setRepayAll(false) }}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${mode === m ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted'}`}
          >
            <TokenIcon symbol="USDC" mint={MINTS.USDC} size={14} />
            {m === 'borrow' ? 'Borrow USDC' : 'Repay USDC'}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-fringe bg-surface-hover p-3">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider text-gray-500">
            {mode === 'borrow' ? 'Borrow amount (USDC)' : 'Repay amount (USDC)'}
          </label>
          {mode === 'repay' && pos && pos.borrowAmount > 0 && (
            <button type="button" onClick={onMaxRepay} className="text-[10px] text-accent-purple hover:underline">
              {canCloseFully ? 'MAX (close)' : `MAX ${(Number(splUsdc.decimal) || 0).toFixed(2)}`}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TokenIcon symbol="USDC" mint={MINTS.USDC} size={18} />
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={repayAll ? '' : amount}
            disabled={repayAll}
            onChange={e => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none disabled:text-gray-400"
          />
        </div>
        {mode === 'repay' && (
          <label className="mt-1 flex items-center gap-2 text-[11px] text-ink-muted">
            <input type="checkbox" checked={repayAll} onChange={e => { setRepayAll(e.target.checked); if (e.target.checked) setAmount('') }} />
            Repay all debt
          </label>
        )}
      </div>

      <button
        type="button"
        disabled={!canSubmit || result.kind === 'pending'}
        onClick={submit}
        className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
      >
        {result.kind === 'pending'
          ? 'Working…'
          : insufficientSol ? 'Top up SOL to borrow'
          : !connected ? (mode === 'borrow' ? 'Connect & Borrow' : 'Connect & Repay')
          : mode === 'borrow' ? 'Sign & Borrow' : 'Sign & Repay'}
      </button>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />
    </div>
  )
}

function PosCard({ title, mint, symbol, amount, usd, rate, rateClass }: {
  title: string; mint: string; symbol: string; amount: string; usd: string; rate: string; rateClass: string;
}) {
  return (
    <div className="rounded-lg bg-surface-hover p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{title}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <TokenIcon symbol={symbol} mint={mint} size={16} />
        <span className="text-sm font-semibold text-ink">{amount} {symbol}</span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>{usd}</span>
        {rate && <span className={rateClass}>{rate}</span>}
      </div>
    </div>
  )
}
